/**
 * The bounded wait between "hand this borrower off" and actually posting the handoff.
 *
 * LogRocket only issues a session URL once its backend has accepted the session, which is
 * 1-5s after init, while the pages that perform a handoff leave in about a second: 68% of
 * production claims arrived carrying no session at all, and a `getSessionURL` callback
 * that lands after the form was submitted can no longer reach it. The claim is one-shot
 * and immutable, so that null is permanent and Nova falls back to a nearby session for
 * the rest of the row's life.
 *
 * So the submit gets a short, bounded grace - never a dependency:
 *
 *  - a recording already in hand submits immediately, synchronously, as before;
 *  - otherwise the capture gets at most `maxWaitMs` to answer, and whichever of the two
 *    happens first submits;
 *  - a capture that never answers, answers with nothing, or rejects still submits. A
 *    missing recording must never cost a borrower their handoff.
 *
 * Timers are injected so the whole matrix runs under vitest's fake timers, and nothing
 * here touches the DOM or a clock: both callers (the Pause page and the recovery page)
 * hand in their own `submit`.
 */

// Two seconds: long enough for the overwhelming majority of getSessionURL callbacks,
// short enough that a borrower reads it as part of the redirect, and trivially inside the
// 15-minute TTL of the signed handoff action (MarketplaceHandoffUrlService).
export const HANDOFF_CAPTURE_GRACE_MS = 2000;

/**
 * What the capture had actually done by the time a handoff was posted.
 *
 * Sent with both handoff POSTs and only ever logged - never stored, never validated,
 * never branched on - so an unforeseen value costs nothing. It exists because a null
 * session tells you only that something went wrong: the recovery page failed for a week
 * in production without a single line saying so, since "no session" looks identical
 * whether the SDK was blocked, the click beat the callback, or nothing was recorded at
 * all. One vocabulary lives here so both submit sites report the same words.
 */
export const HANDOFF_CAPTURE_STATE = {
    /** A URL was held, by capture path. */
    LIVE: 'live',
    CACHE: 'cache',
    ACTIVE_TAB: 'active_tab',
    /** A URL was held under a source this vocabulary does not know. */
    UNKNOWN_SOURCE: 'unknown_source',
    /** The callback answered, but with nothing usable. */
    CALLBACK_EMPTY: 'callback_empty',
    /** The grace expired with the callback still in flight. */
    GRACE_TIMEOUT: 'grace_timeout',
    /** Nothing could be captured at all. */
    NO_SDK: 'no_sdk',
    PRINCIPAL_UNSAFE: 'principal_unsafe',
    /** Recovery only: the SDK was there but never reached a usable recording. */
    INIT_FAILED: 'init_failed',
    NO_ISOLATION: 'no_isolation',
};

export function submitWithCaptureGrace(captureSettled, submit, options = {}) {
    const {
        maxWaitMs = HANDOFF_CAPTURE_GRACE_MS,
        hasUrl = () => false,
        setTimeoutFn = (...args) => setTimeout(...args),
        clearTimeoutFn = (...args) => clearTimeout(...args),
    } = options;

    let submitted = false;

    // The one guarantee this helper owes its callers: the handoff is posted exactly once,
    // whether the capture answered first or the grace expired first.
    const submitOnce = () => {
        if (submitted) {
            return undefined;
        }

        submitted = true;

        return submit();
    };

    const waitable = captureSettled && typeof captureSettled.then === 'function';

    // Nothing to wait for - a recording is already held, capture cannot answer at all, or
    // the caller disabled the grace - so keep the original synchronous behaviour.
    if (hasUrl() || !waitable || !(maxWaitMs > 0)) {
        return Promise.resolve(submitOnce());
    }

    return new Promise(resolve => {
        const timer = setTimeoutFn(() => resolve(submitOnce()), maxWaitMs);

        const settle = () => {
            clearTimeoutFn(timer);
            resolve(submitOnce());
        };

        // A rejected capture is the same answer as a silent one: submit what is held.
        captureSettled.then(settle, settle);
    });
}
